import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-73ed0838"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "interaction-overlay" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "cursor-icon" }, "🖱️", -1))
const _hoisted_3 = {
  key: 0,
  class: "cursor-tooltip"
}
const _hoisted_4 = {
  key: 2,
  class: "drag-trail",
  width: "100%",
  height: "100%"
}
const _hoisted_5 = ["d"]

import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import type { PecTutorConfig } from '../types';

// Props
interface Props {
  config: PecTutorConfig;
  interactionData?: {
    type: string;
    selector?: string;
    from?: string;
    to?: string;
    description?: string;
    options?: Record<string, unknown>;
    duration?: number;
    onComplete?: () => void;
  } | null;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'InteractionOverlay',
  props: {
    config: {},
    interactionData: {}
  },
  setup(__props: any) {

const props = __props;

// Emits
/*
const emit = defineEmits<{
  complete: [];
}>();
*/

// Reactive state
const showCursor = ref(false);
const showRipple = ref(false);
const showTrail = ref(false);
const highlightElement = ref<HTMLElement | null>(null);

const cursorPosition = ref({ x: 0, y: 0 });
const ripplePosition = ref({ x: 0, y: 0 });
const dragPath = ref<Array<{ x: number; y: number }>>([]);

// Computed properties
const cursorStyles = computed(() => ({
  left: `${cursorPosition.value.x}px`,
  top: `${cursorPosition.value.y}px`,
  transform: 'translate(-50%, -50%)',
}));

const rippleStyles = computed(() => ({
  left: `${ripplePosition.value.x}px`,
  top: `${ripplePosition.value.y}px`,
  transform: 'translate(-50%, -50%)',
}));

const highlightStyles = ref<Record<string, string>>({});
let scrollHandler: (() => void) | null = null;

const pathData = computed(() => {
  if (dragPath.value.length < 2) return '';
  
  let d = `M ${dragPath.value[0].x} ${dragPath.value[0].y}`;
  for (let i = 1; i < dragPath.value.length; i++) {
    d += ` L ${dragPath.value[i].x} ${dragPath.value[i].y}`;
  }
  return d;
});

// Methods
const findElement = (selector: string): HTMLElement | null => {
  try {
    return document.querySelector(selector);
  } catch {
    console.warn('PecTutor: Invalid selector:', selector);
    return null;
  }
};

const getElementCenter = (element: HTMLElement): { x: number; y: number } => {
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2
  };
};

const animateCursorToElement = async (selector: string): Promise<void> => {
  const element = findElement(selector);
  if (!element) {
    console.warn('PecTutor: Element not found:', selector);
    return;
  }

  const target = getElementCenter(element);
  const current = cursorPosition.value;
  
  // Highlight the target element
  highlightElement.value = element;
  
  return new Promise((resolve) => {
    const duration = 1000; // 1 second animation
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out)
      const eased = 1 - Math.pow(1 - progress, 3);
      
      cursorPosition.value = {
        x: current.x + (target.x - current.x) * eased,
        y: current.y + (target.y - current.y) * eased
      };
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        resolve();
      }
    };
    
    animate();
  });
};

const simulateClick = async (position: { x: number; y: number }): Promise<void> => {
  // Show ripple effect
  ripplePosition.value = position;
  showRipple.value = true;
  
  // Hide ripple after animation
  setTimeout(() => {
    showRipple.value = false;
  }, 600);
};

const simulateDrag = async (from: { x: number; y: number }, to: { x: number; y: number }): Promise<void> => {
  dragPath.value = [from];
  showTrail.value = true;
  
  const duration = 1500; // 1.5 seconds
  const startTime = Date.now();
  
  return new Promise((resolve) => {
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function
      const eased = 1 - Math.pow(1 - progress, 2);
      
      const currentPoint = {
        x: from.x + (to.x - from.x) * eased,
        y: from.y + (to.y - from.y) * eased
      };
      
      cursorPosition.value = currentPoint;
      dragPath.value.push({ ...currentPoint });
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Clear trail after delay
        setTimeout(() => {
          showTrail.value = false;
          dragPath.value = [];
        }, 1000);
        resolve();
      }
    };
    
    animate();
  });
};

const clearHighlight = () => {
  highlightElement.value = null;
};

const updateHighlightStyles = () => {
  if (!highlightElement.value) {
    highlightStyles.value = {};
    return;
  }
  const rect = highlightElement.value.getBoundingClientRect();
  highlightStyles.value = {
    left: `${rect.left - 5}px`,
    top: `${rect.top - 5}px`,
    width: `${rect.width + 10}px`,
    height: `${rect.height + 10}px`,
    opacity: '1',
    transition: 'all 0.3s ease',
  };
};

// Watch for interaction changes
watch(() => props.interactionData, async (newData, oldData) => {
  // Clean up previous interaction's listeners
  if (oldData && scrollHandler) {
    window.removeEventListener('scroll', scrollHandler);
    scrollHandler = null;
  }

  if (!newData) {
    return;
  }
  
  showCursor.value = true;
  
  try {
    switch (newData.type) {
      case 'click': {
        if (newData.selector) {
          const element = findElement(newData.selector);
          if (element) {
            await animateCursorToElement(newData.selector);
            await simulateClick(cursorPosition.value);
            
            // Actually perform the click
            element.click();
          } else {
            console.warn(`PecTutor: Click failed, element not found for selector: ${newData.selector}`);
          }
        }
        break;
      }
      case 'drag': {
        if (newData.from && newData.to) {
          const fromElement = findElement(newData.from);
          const toElement = findElement(newData.to);
          
          if (fromElement && toElement) {
            const fromPos = getElementCenter(fromElement);
            const toPos = getElementCenter(toElement);
            
            cursorPosition.value = fromPos;
            await simulateDrag(fromPos, toPos);
          }
        }
        break;
      }
      case 'highlight': {
        if (newData.selector) {
          const element = findElement(newData.selector);
          if (element) {
            highlightElement.value = element;

            // Check if element is in viewport
            const rect = element.getBoundingClientRect();
            const isInViewport = (
              rect.top >= 0 &&
              rect.left >= 0 &&
              rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
              rect.right <= (window.innerWidth || document.documentElement.clientWidth)
            );

            if (!isInViewport) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Wait for the smooth scroll to finish
              await new Promise(resolve => setTimeout(resolve, 500));
            }

            // Set initial position and start listening for scroll
            updateHighlightStyles();
            scrollHandler = updateHighlightStyles;
            window.addEventListener('scroll', scrollHandler, { passive: true });

            // Wait for the specified duration
            await new Promise(resolve => setTimeout(resolve, newData.duration || 2000));
          } else {
            console.warn(`PecTutor: Highlight element not found: ${newData.selector}`);
          }
        }
        break;
      }
    }
  } catch (error) {
    console.error('PecTutor interaction error:', error);
  } finally {
    showCursor.value = false;
    highlightStyles.value = { ...highlightStyles.value, opacity: '0' };
    
    // Clean up scroll listener
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler);
      scrollHandler = null;
    }
    
    // Delay completion to allow for fade-out animation
    setTimeout(() => {
      clearHighlight();
      if (props.interactionData?.onComplete) {
        props.interactionData.onComplete();
      }
    }, 300);
  }
}, { immediate: true });

// Lifecycle
onMounted(() => {
  // Initialize cursor position to center of screen
  cursorPosition.value = {
    x: window.innerWidth / 2,
    y: window.innerHeight / 2
  };
});

onUnmounted(() => {
  clearHighlight();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (showCursor.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "animated-cursor",
          style: _normalizeStyle(cursorStyles.value)
        }, [
          _hoisted_2,
          (_ctx.interactionData.description)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_ctx.interactionData.description), 1))
            : _createCommentVNode("", true)
        ], 4))
      : _createCommentVNode("", true),
    (showRipple.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: "click-ripple",
          style: _normalizeStyle(rippleStyles.value)
        }, null, 4))
      : _createCommentVNode("", true),
    (showTrail.value && dragPath.value.length > 1)
      ? (_openBlock(), _createElementBlock("svg", _hoisted_4, [
          _createElementVNode("path", {
            d: pathData.value,
            stroke: "#007bff",
            "stroke-width": "3",
            fill: "none",
            "stroke-linecap": "round",
            "stroke-dasharray": "5,5",
            class: "trail-path"
          }, null, 8, _hoisted_5)
        ]))
      : _createCommentVNode("", true),
    (highlightElement.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 3,
          class: "element-highlight",
          style: _normalizeStyle(highlightStyles.value)
        }, null, 4))
      : _createCommentVNode("", true)
  ]))
}
}

})